import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import "dayjs/locale/ja";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { actions } from "app/configSlice";
import { Asset, DialogType } from "app/types";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useFetchUserData } from "app/hook";
import { deleteAsset, useUserConfig, useUpdateUserConfig } from "app/api";
import { useAssetCache } from "common/hook";
import { UserAvatar, Loading } from "common/elements";
import { usePlaySound, btnDelay, isDebugMode } from "common/utils";
import i18n from "features/parent/i18n";
import { ApiPieceListItem } from "features/builder/types";
import { UserType, AccountType } from "features/user/types";
import { actions as userActions } from "features/user/slice";
import { ProjectEntity, ProjectType } from "features/creator/types";
import { useAccountLimits, fetchAccountPlan } from "features/subscription";
import { parentActions, selectAllParentChatRooms } from "features/parent/slice";
import { getUserAssets, deleteChild, updateUser, updateChildStatus } from "features/user/api";
import {
  AIChatSession,
  ParentPieceReport,
  ParentPieceComment,
  ParentReportTarget,
  FeedbackHistoryItem,
} from "features/parent/types";
import {
  getPiece,
  getProject,
  getUserPieces,
  getUserPieceReports,
  getFeedbackHistory,
  getUserActivities,
  getUserProjects,
  getUserStatus,
  getChildAIChatSessions,
  getUserPieceComments,
} from "features/parent/api";
import { CreatePlayerModal } from "../components/CreatePlayerModal";
import {
  AIChatListModal,
  PlayerIconEditor,
  GenderEditModal,
  BirthdayEditModal,
  ProjectListModal,
  ActivityListModal,
  AIChatDetailModal,
  AssetPreviewModal,
  FeedbackListModal,
  LanguageEditModal,
  ChildNameEditModal,
  FeedbackDetailModal,
  ProjectPreviewModal,
  AIChatHistorySection,
  AssetListModal,
  AssetTabType,
  FeedbackHistorySection,
  ProfileFieldEditModal,
  ProfileFieldType,
  AppearanceEditModal,
  ParentConfirmModal,
  ParentWarningModal,
  ChildUserLimitModal,
  ParentProfileTab,
  PARENT_PROFILE_TAB_OPTIONS,
  ChildSelector,
  StatsCards,
  ProjectsSection,
  PiecesSection,
  PieceListModal,
  PiecePreviewModal,
  PieceReportsSection,
  PieceReportListModal,
  PieceReportDetailModal,
  PieceCommentsSection,
  PieceCommentListModal,
  PieceCommentDetailModal,
  AssetsSection,
  DangerZone,
  RecentActivitySection,
  BasicInfoSection,
  FavoritesSection,
  AppearanceSection,
  SettingsSection,
} from "../components";

export const PlayerTab = ({ scrollToTop }: { scrollToTop?: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();
  const users = useSelector((state: RootState) => state.user.users);
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);
  const selectedChildUid = useSelector((state: RootState) => state.parent.selectedChildUid);
  const chatRooms = useSelector(selectAllParentChatRooms);
  const accountLimits = useAccountLimits();
  const handleDialog = useDialog();
  const { onAssetDeleted } = useAssetCache();

  const [change, setChange] = useState(false);
  const [changeMaxWarning, setChangeMaxWarning] = useState(false);
  const [changeMinWarning, setChangeMinWarning] = useState(false);
  const [changeActiveWarning, setChangeActiveWarning] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [birthdayModalOpen, setBirthdayModalOpen] = useState(false);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [addChildLimitWarning, setAddChildLimitWarning] = useState(false);
  const [deleteChildConfirm, setDeleteChildConfirm] = useState(false);
  const [deleteChildMinWarning, setDeleteChildMinWarning] = useState(false);
  const [profileTab, setProfileTab] = useState(ParentProfileTab.BASIC);
  const [editingField, setEditingField] = useState<ProfileFieldType | null>(null);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [selectedAssetTabs, setSelectedAssetTabs] = useState<Set<AssetTabType>>(
    new Set([AssetTabType.ALBUM, AssetTabType.AI_IMAGE, AssetTabType.SKETCHBOOK]),
  );
  const [selectedProject, setSelectedProject] = useState<ProjectEntity | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [projectListOpen, setProjectListOpen] = useState(false);
  const [pieceListOpen, setPieceListOpen] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState<ApiPieceListItem | null>(null);
  const [selectedPieceComment, setSelectedPieceComment] = useState<ParentPieceComment | null>(null);
  const [pieceReportListOpen, setPieceReportListOpen] = useState(false);
  const [selectedPieceReport, setSelectedPieceReport] = useState<ParentPieceReport | null>(null);
  const [pieceCommentListOpen, setPieceCommentListOpen] = useState(false);
  const [assetListOpen, setAssetListOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackHistoryItem | null>(null);
  const [feedbackListOpen, setFeedbackListOpen] = useState(false);
  const [selectedAIChatSession, setSelectedAIChatSession] = useState<AIChatSession | null>(null);
  const [aiChatListOpen, setAIChatListOpen] = useState(false);

  const childUsers = users.filter((u) => u.account_type !== "parents");

  const selectedUser = useMemo(() => {
    if (selectedChildUid) {
      const found = childUsers.find((u) => u.uid === selectedChildUid);
      if (found) return found;
    }
    const activeUser = childUsers.find((u) => u.uid === active?.uid);
    if (activeUser) return activeUser;
    return childUsers[0];
  }, [selectedChildUid, childUsers, active]);

  useEffect(() => {
    if (!selectedChildUid && selectedUser) {
      dispatch(parentActions.setSelectedChildUid(selectedUser.uid));
    }
  }, [selectedChildUid, selectedUser, dispatch]);

  useEffect(() => {
    if (searchParams.get("action") !== "create-player") return;
    setAddChildModalOpen(true);
  }, [searchParams, setSearchParams]);

  const { data: userConfig } = useUserConfig(selectedUser?.uid ?? "");
  const { mutate: updateUserConfig } = useUpdateUserConfig(selectedUser?.uid ?? "");

  const { data: projects = [] } = useQuery<ProjectEntity[]>({
    queryKey: ["parent/getUserProjects", selectedUser?.uid],
    queryFn: () => getUserProjects(selectedUser.uid),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const { data: pieces = [] } = useQuery<ApiPieceListItem[]>({
    queryKey: ["parent/getUserPieces", selectedUser?.uid],
    queryFn: () => getUserPieces(selectedUser.uid),
    // つくってパズル（ピースビルダー）は本番に出さないので、ピースの一覧・コメントはデバッグモードのときだけ
    enabled: !!selectedUser?.uid && isDebugMode,
    staleTime: 5 * 60 * 1000,
  });

  const { data: pieceComments = [] } = useQuery<ParentPieceComment[]>({
    queryKey: ["parent/getUserPieceComments", selectedUser?.uid],
    queryFn: () => getUserPieceComments(selectedUser.uid),
    enabled: !!selectedUser?.uid && isDebugMode,
    staleTime: 5 * 60 * 1000,
  });

  // コメントから、書いた先のピースの詳細をひらく。
  // 他人のピースにも書けるので、手元の一覧に無ければ取りに行く
  const handleOpenCommentPiece = async () => {
    const comment = selectedPieceComment;
    if (!comment) return;
    setSelectedPieceComment(null);
    const own = pieces.find((piece) => piece.piece_id === comment.piece_id);
    if (own) {
      setSelectedPiece(own);
      return;
    }
    try {
      setSelectedPiece(await getPiece(comment.piece_id));
    } catch {
      toast.warn(i18n.t("MSG_PARENT_PAGE_PLAYER_PIECE_LOAD_FAILED"));
    }
  };

  const { data: pieceReports = [] } = useQuery<ParentPieceReport[]>({
    queryKey: ["parent/getUserPieceReports", selectedUser?.uid],
    queryFn: () => getUserPieceReports(selectedUser.uid),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  // 通報したアプリをひらく。手元の一覧（自分のもの＋みんなのアプリ）に無ければ取りに行く。
  // 通報したあとにブロックされると一覧から消えるため
  const openReportProject = async (projectId: string) => {
    const listed = projects.find((project) => project.project_id === projectId);
    if (listed) {
      setSelectedProject(listed);
      return;
    }
    try {
      setSelectedProject(await getProject(projectId));
    } catch {
      toast.warn(i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_LOAD_FAILED"));
    }
  };

  const handleOpenReportPiece = async () => {
    const report = selectedPieceReport;
    if (!report) return;
    setSelectedPieceReport(null);
    if (report.target === ParentReportTarget.PROJECT) {
      if (report.project_id) await openReportProject(report.project_id);
      return;
    }
    if (!report.piece_id) return;
    const own = pieces.find((piece) => piece.piece_id === report.piece_id);
    if (own) {
      setSelectedPiece(own);
      return;
    }
    try {
      setSelectedPiece(await getPiece(report.piece_id));
    } catch {
      toast.warn(i18n.t("MSG_PARENT_PAGE_PLAYER_PIECE_LOAD_FAILED"));
    }
  };

  const { data: assetsData } = useQuery({
    queryKey: ["creator/getUserAssets", selectedUser?.uid],
    queryFn: () => getUserAssets({ uid: selectedUser.uid }),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });
  const assets = assetsData?.assets ?? [];

  const { data: feedbackHistory = [] } = useQuery<FeedbackHistoryItem[]>({
    queryKey: ["parent/getFeedbackHistory", selectedUser?.uid],
    queryFn: () => getFeedbackHistory({ uid: selectedUser.uid }),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const { data: status } = useQuery({
    queryKey: ["parent/getUserStatus", selectedUser?.uid],
    queryFn: () => getUserStatus(selectedUser!.uid),
    enabled: !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const { data: aiChatSessionsData } = useQuery({
    queryKey: ["parent/getChildAIChatSessions", parent?.uid, selectedUser?.uid],
    queryFn: () =>
      getChildAIChatSessions({
        parentUid: parent!.uid,
        childUid: selectedUser!.uid,
      }),
    enabled: !!parent?.uid && !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const aiChatSessions = aiChatSessionsData?.sessions ?? [];

  const { data: activitiesData } = useQuery({
    queryKey: ["parent/getUserActivities", parent?.uid, selectedUser?.uid],
    queryFn: () =>
      getUserActivities({
        parentUid: parent!.uid,
        childUid: selectedUser!.uid,
        limit: 50,
      }),
    enabled: !!parent?.uid && !!selectedUser?.uid,
    staleTime: 5 * 60 * 1000,
  });

  const activities = useMemo(() => {
    const allActivities = activitiesData?.activities ?? [];
    const now = dayjs();
    return allActivities.filter((activity) => {
      const activityTime = dayjs(activity.activity_at);
      return now.diff(activityTime, "hour") < 24;
    });
  }, [activitiesData]);

  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [showFrozenWarning, setShowFrozenWarning] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const { handleFetchUserData } = useFetchUserData();

  const filteredProjects = useMemo(() => {
    if (!selectedUser?.uid) return [];
    return projects
      .filter(
        (p) => !p.deleted_at && p.type === ProjectType.BASIC && p.author_uid === selectedUser.uid,
      )
      .sort((a, b) => dayjs(b.created_time).unix() - dayjs(a.created_time).unix());
  }, [projects, selectedUser?.uid]);

  const handleSelectChild = (uid: string) => {
    play();
    dispatch(parentActions.setSelectedChildUid(uid));
  };

  const handleAddChild = () => {
    play();
    const kidsCount = users.filter((u) => u.account_type !== AccountType.PARENTS).length;
    if (kidsCount >= (accountLimits?.players?.limit ?? 1)) {
      setAddChildLimitWarning(true);
    } else {
      setAddChildModalOpen(true);
    }
  };

  const handleDeleteChild = () => {
    if (!selectedUser) return;
    play();

    // アクティブなキッズアカウント数をカウント
    const activeKidsCount = users.filter(
      (u) => u.account_type !== AccountType.PARENTS && u.type !== UserType.FROZEN,
    ).length;

    if (activeKidsCount <= 1) {
      setDeleteChildMinWarning(true);
    } else {
      setDeleteChildConfirm(true);
    }
  };

  const handleConfirmDeleteChild = () => {
    if (!selectedUser || !parent) return;
    play();
    setDeleteChildConfirm(false);

    dispatch(deleteChild({ uid: selectedUser.uid, parent_uid: parent.uid }))
      .unwrap()
      .then(() => {
        // 削除されたプレイヤーのチャットルームを削除
        const chatRoomToRemove = chatRooms.find((room) =>
          room.members.some((m) => m.uid === selectedUser.uid),
        );
        if (chatRoomToRemove) {
          dispatch(parentActions.removeParentChatRoom(chatRoomToRemove.id));
        }

        // limitsを再取得（削除された子供のlimitsが消える）
        dispatch(fetchAccountPlan({ uid: parent.uid }));

        // 削除後、最初のユーザーを選択
        const remainingUsers = childUsers.filter((u) => u.uid !== selectedUser.uid);
        if (remainingUsers.length > 0) {
          dispatch(parentActions.setSelectedChildUid(remainingUsers[0].uid));
        }
        scrollToTop?.();
      });
  };

  const handleOpenLanguageModal = () => {
    if (!userConfig || !selectedUser) return;
    play();
    setLanguageModalOpen(true);
  };

  const handleTogglePrivacyPublish = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.project_publish;
    const newConfig = { ...userConfig, project_publish: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "project_publish", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleTogglePrivacyCopy = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.project_copied;
    const newConfig = { ...userConfig, project_copied: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "project_copied", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleToggleAiChat = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.ai_chat;
    const newConfig = { ...userConfig, ai_chat: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "ai_chat", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleToggleAiGeneration = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.ai_generation;
    const newConfig = { ...userConfig, ai_generation: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "ai_generation", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleTogglePiecePublish = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.piece_publish;
    const newConfig = { ...userConfig, piece_publish: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "piece_publish", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleTogglePieceCopy = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.piece_copy;
    const newConfig = { ...userConfig, piece_copy: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "piece_copy", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleToggleComment = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.comment_enabled;
    const newConfig = { ...userConfig, comment_enabled: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "comment_enabled", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleToggleShakeBugReport = () => {
    if (!userConfig || !selectedUser) return;
    play();
    const newValue = !userConfig.shake_bug_report;
    const newConfig = { ...userConfig, shake_bug_report: newValue };
    if (selectedUser.uid === active?.uid) {
      dispatch(actions.updateUserConfig({ key: "shake_bug_report", value: newValue }));
    }
    updateUserConfig(newConfig);
  };

  const handleOpenChangeUserStatus = () => {
    if (!selectedUser) return;
    play();

    // ログイン中ユーザーの無効化を禁止
    if (selectedUser.uid === active?.uid && selectedUser.type !== UserType.FROZEN) {
      setChangeActiveWarning(true);
      return;
    }

    const kidsCount = users.filter((u) => u.account_type !== AccountType.PARENTS).length;

    if (selectedUser.type === UserType.FROZEN) {
      if (kidsCount >= (accountLimits?.players?.limit ?? 1)) {
        setChangeMaxWarning(true);
      } else {
        setChange(true);
      }
    } else {
      if (kidsCount === 1) {
        setChangeMinWarning(true);
      } else {
        setChange(true);
      }
    }
  };

  const handleChangeUserStatus = () => {
    if (!selectedUser) return;
    btnDelay(async () => {
      play();
      const newType = selectedUser.type === UserType.FROZEN ? UserType.SUB : UserType.FROZEN;
      try {
        await dispatch(updateChildStatus({ uid: selectedUser.uid, type: newType })).unwrap();
      } catch (error) {
        console.error("Failed to update child status:", error);
      }
      setChange(false);
    });
  };

  const handleCloseDialog = () => {
    btnDelay(() => {
      play();
      setChange(false);
      setChangeMaxWarning(false);
      setChangeMinWarning(false);
      setChangeActiveWarning(false);
    });
  };

  const handleSwitchActiveUser = () => {
    if (!selectedUser || !parent) return;

    // FROZENユーザーは切り替え不可
    if (selectedUser.type === UserType.FROZEN) {
      setShowFrozenWarning(true);
      return;
    }

    // 確認ダイアログを表示
    setShowSwitchConfirm(true);
  };

  const handleConfirmSwitch = () => {
    if (!selectedUser || !parent) return;
    setShowSwitchConfirm(false);
    play();
    setSwitchLoading(true);
    handleFetchUserData({ parent_uid: parent.uid, uid: selectedUser.uid })
      .then(() => {
        dispatch(userActions.setActiveUser(selectedUser));
        setSwitchLoading(false);
      })
      .catch((error) => {
        console.error("Failed to switch active user:", error);
        setSwitchLoading(false);
      });
  };

  const handleToggleAssetTab = (tab: AssetTabType) => {
    setSelectedAssetTabs((prev) => {
      const newTabs = new Set(prev);
      if (newTabs.has(tab)) {
        newTabs.delete(tab);
      } else {
        newTabs.add(tab);
      }
      return newTabs;
    });
  };

  const handleDeleteAsset = (asset: Asset) => {
    if (!selectedUser) return;
    handleDialog({ type: DialogType.LOADING, value: true });
    deleteAsset({ id: asset.id })
      .then(() => {
        onAssetDeleted(selectedUser.uid, asset.id, asset.file_size || 0);
        setSelectedAsset(null);
        toast(
          <div>
            <p className="text text-textcolor-black">{i18n.t("MSG_TOAST_DELETED_SUCCESS")}</p>
          </div>,
        );
      })
      .catch((e) => {
        console.error(e);
        toast(
          <div>
            <p className="text text-textcolor-black">{i18n.t("MSG_TOAST_ERROR")}</p>
          </div>,
        );
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  return (
    <>
      {/* ローディング */}
      {switchLoading && <Loading />}

      {/* FROZEN警告ダイアログ */}
      <ParentWarningModal
        open={showFrozenWarning}
        title={i18n.t("MSG_PARENT_PAGE_FROZEN_DIALOG_TITLE")}
        message={i18n.t("MSG_PARENT_PAGE_FROZEN_DIALOG_MESSAGE")}
        onClose={() => setShowFrozenWarning(false)}
      />

      {/* プレイヤー切り替え確認ダイアログ */}
      <ParentConfirmModal
        open={showSwitchConfirm}
        icon="swap_horiz"
        title={i18n.t("MSG_PARENT_PAGE_SWITCH_CONFIRM_TITLE")}
        message={i18n.t("MSG_PARENT_PAGE_SWITCH_CONFIRM_MESSAGE", {
          name: selectedUser?.display_name,
        })}
        onClose={() => setShowSwitchConfirm(false)}
        onConfirm={handleConfirmSwitch}
      />

      {/* アカウント状態変更ダイアログ */}
      <ParentConfirmModal
        open={change}
        icon="info"
        title={
          selectedUser?.type === UserType.FROZEN
            ? i18n.t("MSG_SETTING_PARENTS_ENABLE_KIDS_USER_TITLE")
            : i18n.t("MSG_SETTING_PARENTS_DISABLE_KIDS_USER_TITLE")
        }
        message={
          selectedUser?.type === UserType.FROZEN
            ? i18n.t("MSG_SETTING_PARENTS_ENABLE_KIDS_USER_MESSAGE", {
                name: selectedUser?.display_name,
              })
            : i18n.t("MSG_SETTING_PARENTS_DISABLE_KIDS_USER_MESSAGE", {
                name: selectedUser?.display_name,
              })
        }
        onClose={handleCloseDialog}
        onConfirm={handleChangeUserStatus}
      />
      <ParentWarningModal
        open={changeMaxWarning}
        title={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MAX_WARNING_TITLE")}
        message={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MAX_WARNING_MESSAGE")}
        onClose={handleCloseDialog}
      />
      <ParentWarningModal
        open={changeMinWarning}
        title={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MIN_WARNING_TITLE")}
        message={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MIN_WARNING_MESSAGE")}
        onClose={handleCloseDialog}
      />
      <ParentWarningModal
        open={changeActiveWarning}
        title={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_ACTIVE_WARNING_TITLE")}
        message={i18n.t("MSG_SETTING_PARENTS_CHANGE_KIDS_USER_ACTIVE_WARNING_MESSAGE")}
        onClose={handleCloseDialog}
      />

      <div className="flex-col-el gap-6">
        {/* 子供セレクター（子どもがいる場合のみ表示） */}
        {childUsers.length > 0 && (
          <ChildSelector
            childUsers={childUsers}
            selectedUser={selectedUser}
            activeUid={active?.uid}
            onSelectChild={handleSelectChild}
            onAddChild={handleAddChild}
          />
        )}

        {/* 子ども0人の場合のEmpty State */}
        {childUsers.length === 0 && (
          <div className="flex-col-el flex-center bg-white rounded-[16px] p-12 min-h-[400px]">
            <span className="material-symbols-outlined !text-[80px] text-gray2-60 mb-4">group</span>
            <h2 className="text-[28px] text-textcolor-black mb-2">
              {i18n.t("MSG_PARENT_PAGE_PLAYER_EMPTY_TITLE")}
            </h2>
            <p className="text-[21px] text-gray2-100 mb-6 text-center">
              {i18n.t("MSG_PARENT_PAGE_PLAYER_EMPTY_MESSAGE")}
            </p>
            <button
              onClick={handleAddChild}
              className="flex-row-el items-center gap-2 bg-blue-100 text-white rounded-[12px] px-6 py-3 text-[21px] border-none cursor-pointer hover:bg-blue-120 transition-all"
            >
              <span className="material-symbols-outlined text-xl">add</span>
              {i18n.t("MSG_PARENT_PAGE_PLAYER_ADD_BTN")}
            </button>
          </div>
        )}

        {selectedUser && (
          <div className="flex-col-el gap-6">
            {/* プロフィールカード */}
            <div className="bg-white rounded-[16px] p-6">
              {/* 上部: アイコン + 名前 + 状態（中央配置） */}
              <div className="flex-col-el items-center gap-3 mb-6">
                {/* 大きなアイコン */}
                <div
                  className="relative cursor-pointer group"
                  onClick={() => {
                    play();
                    btnDelay(() => setIconModalOpen(true));
                  }}
                >
                  <UserAvatar size={100} icon={selectedUser.icon} />
                  <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gray2-100 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <span className="material-symbols-outlined text-[27px] text-white">
                      photo_camera
                    </span>
                  </div>
                </div>
                {/* 名前 + 編集ボタン */}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                  {/* 左: 空（バランス用） */}
                  <div></div>

                  {/* 中央: 名前（常に中央） */}
                  <span className="text-[30px] text-textcolor-black">
                    {selectedUser.display_name}
                  </span>

                  {/* 右: 変更ボタン */}
                  <div className="flex justify-start pl-2">
                    <button
                      onClick={() => {
                        play();
                        btnDelay(() => setNameModalOpen(true));
                      }}
                      className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
                    >
                      {i18n.t("MSG_PARENT_PAGE_CHANGE")}
                    </button>
                  </div>
                </div>
                {/* アクティブ状態バッジ + 切り替えボタン */}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center min-h-[40px]">
                  {/* 左: 空（バランス用） */}
                  <div></div>

                  {/* 中央: バッジ（常に中央） */}
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[18px] ${
                      active?.uid === selectedUser.uid
                        ? "bg-green-60/20 text-green-100"
                        : "bg-gray2-20 text-gray2-100"
                    }`}
                  >
                    <span className="material-symbols-outlined text-[21px]">
                      {active?.uid === selectedUser.uid ? "check_circle" : "pause_circle"}
                    </span>
                    {active?.uid === selectedUser.uid
                      ? i18n.t("MSG_PARENT_PAGE_ACTIVE")
                      : i18n.t("MSG_PARENT_PAGE_INACTIVE")}
                  </span>

                  {/* 右: 切り替えボタン */}
                  <div className="flex justify-start pl-2">
                    <button
                      onClick={() => {
                        play();
                        btnDelay(handleSwitchActiveUser);
                      }}
                      className={`bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all ${
                        active?.uid === selectedUser.uid ? "invisible" : ""
                      }`}
                    >
                      {i18n.t("MSG_PARENT_PAGE_SWITCH_LOGIN")}
                    </button>
                  </div>
                </div>
              </div>

              {/* 下部: 3カラム */}
              <div className="grid grid-cols-[1.1fr_1fr_1fr]">
                {/* 左カラム: 基本情報 */}
                <div className="flex-col-el rounded-[12px] p-6 overflow-hidden">
                  <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black mb-3 pb-3 border-b border-outline">
                    <span className="material-symbols-outlined !text-[45px] text-red-100">
                      person
                    </span>
                    {i18n.t("MSG_PARENT_PAGE_BASIC_INFO")}
                  </div>

                  {/* プロフィールタブ（素材セクションと同じスタイル） */}
                  <div className="flex-row-el items-center justify-between gap-2 mb-4">
                    <div className="flex-row-el gap-2">
                      {PARENT_PROFILE_TAB_OPTIONS.map((option) => {
                        const isActive = profileTab === option.key;
                        return (
                          <button
                            key={option.key}
                            className={`px-4 py-2 rounded-[12px] text-[18px] border-none transition-all cursor-pointer shrink-0 ${
                              isActive
                                ? "bg-ink text-white"
                                : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                            }`}
                            onClick={() => {
                              play();
                              setProfileTab(option.key);
                            }}
                          >
                            {i18n.t(option.labelKey)}
                          </button>
                        );
                      })}
                    </div>
                    {profileTab === ParentProfileTab.APPEARANCE && (
                      <button
                        onClick={() => {
                          play();
                          btnDelay(() => setAppearanceModalOpen(true));
                        }}
                        className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-4 text-[16px] cursor-pointer hover:bg-gray2-20 transition-all shrink-0"
                      >
                        {i18n.t("MSG_PARENT_PAGE_CHANGE")}
                      </button>
                    )}
                  </div>

                  {/* 基本情報タブ */}
                  {profileTab === ParentProfileTab.BASIC && (
                    <BasicInfoSection
                      selectedUser={selectedUser}
                      onEditGender={() => {
                        play();
                        btnDelay(() => setGenderModalOpen(true));
                      }}
                      onEditBirthday={() => setBirthdayModalOpen(true)}
                    />
                  )}

                  {/* 好きなものタブ */}
                  {profileTab === ParentProfileTab.FAVORITES && (
                    <FavoritesSection
                      selectedUser={selectedUser}
                      onEditField={(field) => {
                        play();
                        btnDelay(() => setEditingField(field));
                      }}
                    />
                  )}

                  {/* 外見タブ */}
                  {profileTab === ParentProfileTab.APPEARANCE && (
                    <AppearanceSection selectedUser={selectedUser} />
                  )}
                </div>

                {/* 中央カラム: 設定 */}
                <SettingsSection
                  selectedUser={selectedUser}
                  userConfig={userConfig}
                  onEditLanguage={handleOpenLanguageModal}
                  onTogglePublish={handleTogglePrivacyPublish}
                  onToggleCopy={handleTogglePrivacyCopy}
                  onToggleAiChat={handleToggleAiChat}
                  onToggleAiGeneration={handleToggleAiGeneration}
                  onTogglePiecePublish={handleTogglePiecePublish}
                  onTogglePieceCopy={handleTogglePieceCopy}
                  onToggleComment={handleToggleComment}
                  onToggleShakeBugReport={handleToggleShakeBugReport}
                  onChangeUserStatus={handleOpenChangeUserStatus}
                />

                {/* 右カラム: 最近の活動（24時間以内） */}
                <RecentActivitySection
                  activities={activities}
                  onShowMore={() => setShowActivitiesModal(true)}
                />
              </div>
            </div>

            {/* 統計カード */}
            <StatsCards status={status} />

            {/* 作品一覧セクション */}
            <ProjectsSection
              projects={filteredProjects}
              onSelectProject={(project) => setSelectedProject(project)}
              onShowMore={() => setProjectListOpen(true)}
            />

            {/* ピース一覧・ピースに書いたコメント（つくってパズルはデバッグモードのときだけ） */}
            {isDebugMode && (
              <>
                <PiecesSection
                  pieces={pieces}
                  onSelectPiece={(piece) => setSelectedPiece(piece)}
                  onShowMore={() => setPieceListOpen(true)}
                />
                <PieceCommentsSection
                  comments={pieceComments}
                  onSelectComment={(comment) => setSelectedPieceComment(comment)}
                  onShowMore={() => setPieceCommentListOpen(true)}
                />
              </>
            )}

            {/* 報告した履歴セクション */}
            <PieceReportsSection
              reports={pieceReports}
              onSelectReport={(report) => setSelectedPieceReport(report)}
              onShowMore={() => setPieceReportListOpen(true)}
            />

            {/* 素材セクション */}
            <AssetsSection
              assets={assets}
              selectedAssetTabs={selectedAssetTabs}
              onSelectAsset={(asset) => setSelectedAsset(asset)}
              onShowMore={() => setAssetListOpen(true)}
              onToggleTab={handleToggleAssetTab}
              showSketchbook={true}
            />

            {/* フィードバック履歴セクション */}
            <FeedbackHistorySection
              feedbackItems={feedbackHistory}
              onShowMore={() => setFeedbackListOpen(true)}
              onSelectItem={(item) => setSelectedFeedback(item)}
            />

            {/* AIチャット履歴セクション */}
            <AIChatHistorySection
              sessions={aiChatSessions}
              onShowMore={() => setAIChatListOpen(true)}
              onSelectSession={(session) => setSelectedAIChatSession(session)}
            />

            {/* 危険ゾーン（アクティブユーザー以外のみ表示） */}
            {selectedUser.uid !== active?.uid && <DangerZone onDelete={handleDeleteChild} />}
          </div>
        )}

        {/* 編集モーダル */}
        {selectedUser && (
          <>
            <ChildNameEditModal
              open={nameModalOpen}
              currentName={selectedUser.display_name}
              uid={selectedUser.uid}
              onClose={() => setNameModalOpen(false)}
            />
            <GenderEditModal
              open={genderModalOpen}
              currentGender={selectedUser.gender}
              uid={selectedUser.uid}
              onClose={() => setGenderModalOpen(false)}
            />
            <BirthdayEditModal
              open={birthdayModalOpen}
              currentBirthday={selectedUser.birthday}
              uid={selectedUser.uid}
              onClose={() => setBirthdayModalOpen(false)}
            />
            <PlayerIconEditor
              open={iconModalOpen}
              onClose={() => setIconModalOpen(false)}
              targetUid={selectedUser.uid}
              currentIcon={selectedUser.icon}
              currentGender={selectedUser.gender}
              currentBirthday={selectedUser.birthday}
              currentFavoriteColor={selectedUser.favorite_color}
              currentBestSubject={selectedUser.best_subject}
              currentSelfIntroduction={selectedUser.self_introduction}
              currentAccessory={selectedUser.accessory}
              currentHairstyle={selectedUser.hairstyle}
              currentBodyType={selectedUser.body_type}
              currentFaceShape={selectedUser.face_shape}
              onSelect={(data) => {
                const userInfo: Record<string, unknown> = { icon: data.icon };
                // ProfileAIで変更されたプロフィール情報を反映
                if (data.gender !== undefined) userInfo.gender = data.gender;
                if (data.birthday !== undefined) userInfo.birthday = data.birthday;
                if (data.favorite_color !== undefined)
                  userInfo.favorite_color = data.favorite_color;
                if (data.best_subject !== undefined) userInfo.best_subject = data.best_subject;
                if (data.accessory !== undefined) userInfo.accessory = data.accessory;
                if (data.hairstyle !== undefined) userInfo.hairstyle = data.hairstyle;
                if (data.body_type !== undefined) userInfo.body_type = data.body_type;
                if (data.face_shape !== undefined) userInfo.face_shape = data.face_shape;
                dispatch(
                  updateUser({
                    uid: selectedUser.uid,
                    userInfo,
                  }),
                );
              }}
            />
            {editingField && (
              <ProfileFieldEditModal
                open={!!editingField}
                uid={selectedUser.uid}
                fieldType={editingField}
                currentValue={
                  editingField === ProfileFieldType.SELF_INTRODUCTION
                    ? selectedUser.self_introduction
                    : editingField === ProfileFieldType.FAVORITE_COLOR
                      ? selectedUser.favorite_color
                      : editingField === ProfileFieldType.BEST_SUBJECT
                        ? selectedUser.best_subject
                        : editingField === ProfileFieldType.HAIRSTYLE
                          ? selectedUser.hairstyle
                          : editingField === ProfileFieldType.BODY_TYPE
                            ? selectedUser.body_type
                            : selectedUser.face_shape
                }
                onClose={() => setEditingField(null)}
              />
            )}
            <AppearanceEditModal
              open={appearanceModalOpen}
              uid={selectedUser.uid}
              currentAccessory={selectedUser.accessory}
              currentHairstyle={selectedUser.hairstyle}
              currentBodyType={selectedUser.body_type}
              currentFaceShape={selectedUser.face_shape}
              onClose={() => setAppearanceModalOpen(false)}
            />
          </>
        )}

        {selectedUser && userConfig && (
          <LanguageEditModal
            open={languageModalOpen}
            currentLocale={userConfig.locale}
            activeUid={active?.uid}
            selectedUid={selectedUser.uid}
            userConfig={userConfig}
            updateUserConfig={updateUserConfig}
            onClose={() => setLanguageModalOpen(false)}
          />
        )}

        {/* ユーザー追加モーダル */}
        <CreatePlayerModal
          open={addChildModalOpen}
          onClose={() => setAddChildModalOpen(false)}
          onSuccess={() => setAddChildModalOpen(false)}
        />

        {/* ユーザー追加上限警告 */}
        <ChildUserLimitModal
          open={addChildLimitWarning}
          onClose={() => setAddChildLimitWarning(false)}
          limit={accountLimits?.players?.limit ?? 1}
        />

        {/* ユーザー削除確認 */}
        <ParentConfirmModal
          open={deleteChildConfirm}
          icon="delete"
          title={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_TITLE")}
          message={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_MESSAGE", {
            name: selectedUser?.display_name,
          })}
          onClose={() => setDeleteChildConfirm(false)}
          onConfirm={handleConfirmDeleteChild}
        />

        {/* ユーザー削除最低1人警告 */}
        <ParentWarningModal
          open={deleteChildMinWarning}
          title={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_MIN_TITLE")}
          message={i18n.t("MSG_PARENT_PAGE_DELETE_CHILD_MIN_MESSAGE")}
          onClose={() => setDeleteChildMinWarning(false)}
        />

        {/* 作品・素材モーダル */}
        {selectedProject && (
          <ProjectPreviewModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}

        {selectedAsset && (
          <AssetPreviewModal
            asset={selectedAsset}
            onClose={() => setSelectedAsset(null)}
            onDelete={handleDeleteAsset}
          />
        )}

        {projectListOpen && (
          <ProjectListModal
            projects={filteredProjects}
            onClose={() => setProjectListOpen(false)}
            onSelectProject={(project) => {
              setProjectListOpen(false);
              setSelectedProject(project);
            }}
          />
        )}

        {pieceListOpen && (
          <PieceListModal
            pieces={pieces}
            onClose={() => setPieceListOpen(false)}
            onSelectPiece={(piece) => {
              setPieceListOpen(false);
              setSelectedPiece(piece);
            }}
          />
        )}

        {selectedPiece && (
          <PiecePreviewModal piece={selectedPiece} onClose={() => setSelectedPiece(null)} />
        )}

        {selectedPieceComment && (
          <PieceCommentDetailModal
            comment={selectedPieceComment}
            onClose={() => setSelectedPieceComment(null)}
            onOpenPiece={handleOpenCommentPiece}
          />
        )}

        {pieceCommentListOpen && (
          <PieceCommentListModal
            comments={pieceComments}
            onClose={() => setPieceCommentListOpen(false)}
            onSelectComment={(comment) => {
              setPieceCommentListOpen(false);
              setSelectedPieceComment(comment);
            }}
          />
        )}

        {pieceReportListOpen && (
          <PieceReportListModal
            reports={pieceReports}
            onClose={() => setPieceReportListOpen(false)}
            onSelectReport={(report) => {
              setPieceReportListOpen(false);
              setSelectedPieceReport(report);
            }}
          />
        )}

        {selectedPieceReport && (
          <PieceReportDetailModal
            report={selectedPieceReport}
            onClose={() => setSelectedPieceReport(null)}
            onOpenPiece={handleOpenReportPiece}
          />
        )}

        {assetListOpen && (
          <AssetListModal
            assets={assets}
            initialTabs={selectedAssetTabs}
            onClose={() => setAssetListOpen(false)}
            onSelectAsset={(asset) => {
              setAssetListOpen(false);
              setSelectedAsset(asset);
            }}
            showSketchbook={true}
          />
        )}

        {/* フィードバックモーダル */}
        {selectedFeedback && (
          <FeedbackDetailModal
            item={selectedFeedback}
            childIcon={selectedUser?.icon}
            onClose={() => setSelectedFeedback(null)}
          />
        )}

        {feedbackListOpen && (
          <FeedbackListModal
            feedbackItems={feedbackHistory}
            onClose={() => setFeedbackListOpen(false)}
            onSelectItem={(item) => {
              setFeedbackListOpen(false);
              setSelectedFeedback(item);
            }}
          />
        )}

        {/* AIチャットモーダル */}
        {selectedAIChatSession && (
          <AIChatDetailModal
            session={selectedAIChatSession}
            childIcon={selectedUser?.icon}
            onClose={() => setSelectedAIChatSession(null)}
          />
        )}

        {aiChatListOpen && (
          <AIChatListModal
            sessions={aiChatSessions}
            onClose={() => setAIChatListOpen(false)}
            onSelectSession={(session) => {
              setAIChatListOpen(false);
              setSelectedAIChatSession(session);
            }}
          />
        )}

        {/* 活動履歴モーダル */}
        {showActivitiesModal && (
          <ActivityListModal
            activities={activities}
            onClose={() => setShowActivitiesModal(false)}
          />
        )}
      </div>
    </>
  );
};
